import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Menu, X, ChevronDown, LogOut, Settings, CreditCard, LayoutDashboard } from 'lucide-react';

const MarketingHeader = ({ onLogin, onGetStarted, currentPage, onNavigate }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Features', id: 'features', href: '/#features' },
    { name: 'Solutions', id: 'solutions', href: '/solutions' },
    { name: 'Blog', id: 'blog', href: '/blog' },
    { name: 'Pricing', id: 'pricing', href: '/pricing' },
  ];

  const handleNavigation = (itemId, href) => {
    setMobileMenuOpen(false);
    if (onNavigate) {
      onNavigate(itemId);
    } else {
      window.location.href = href;
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
        ? 'bg-neutral-0/80 backdrop-blur-md shadow-soft py-3'
        : 'bg-[#E9F8F9] py-5'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <button
              onClick={() => handleNavigation('home', '/')}
              className="flex items-center space-x-2 transition-transform hover:scale-105"
            >
              <span className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight">
                <span className="text-[#2E75B6]">Subscription Manager</span>
                <span className="text-[#E8852E]">.pro</span>
              </span>
            </button>
          </div>

          {/* Desktop Navigation - Centered */}
          <nav className="hidden md:flex items-center space-x-1 bg-neutral-100/50 p-1 rounded-pill backdrop-blur-sm border border-neutral-200/50">
            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.id, item.href)}
                className={`px-5 py-2 rounded-pill text-sm font-semibold transition-all ${currentPage === item.id
                  ? 'bg-neutral-0 text-primary-600 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-0/50'
                  }`}
              >
                {item.name}
              </button>
            ))}
          </nav>

          {/* Desktop Right Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {!isAuthenticated ? (
              <>
                <button
                  onClick={onLogin}
                  className="px-5 py-2 text-sm font-bold text-neutral-700 hover:text-primary-600 transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={onGetStarted}
                  className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-neutral-0 text-sm font-bold rounded-xl shadow-soft hover:shadow-strong transition-all transform hover:-translate-y-0.5 active:translate-y-0"
                >
                  Get Started
                </button>
              </>
            ) : (
              <div className="relative group">
                <button className="flex items-center space-x-3 p-1.5 bg-neutral-100 rounded-pill hover:bg-neutral-200 transition-colors">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-neutral-0 shadow-sm">
                    {user?.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <ChevronDown className="w-4 h-4 text-neutral-500 group-hover:rotate-180 transition-transform duration-300" />
                </button>

                <div className="absolute right-0 mt-3 w-56 bg-neutral-0 rounded-xl shadow-strong border border-neutral-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 p-2 origin-top-right">
                  <div className="px-4 py-3 border-b border-neutral-50 mb-1">
                    <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Account</p>
                    <p className="text-sm font-bold text-neutral-900 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => handleNavigation('app', '/app')}
                    className="flex items-center space-x-3 w-full px-4 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    <span>Dashboard</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('settings', '/settings')}
                    className="flex items-center space-x-3 w-full px-4 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Settings</span>
                  </button>
                  <button
                    onClick={() => handleNavigation('subscription', '/subscription')}
                    className="flex items-center space-x-3 w-full px-4 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors"
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>Subscription</span>
                  </button>
                  <div className="h-px bg-neutral-50 my-1"></div>
                  <button
                    onClick={logout}
                    className="flex items-center space-x-3 w-full px-4 py-2.5 text-sm font-semibold text-error/80 hover:bg-error/5 hover:text-error rounded-lg transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-neutral-600 hover:bg-neutral-100 rounded-xl transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 bg-neutral-0 border-t border-neutral-100 transition-all duration-300 overflow-hidden ${mobileMenuOpen ? 'max-h-screen opacity-100 shadow-strong' : 'max-h-0 opacity-0'
          }`}
      >
        <div className="px-4 py-6 space-y-2">
          {navigation.map((item) => (
            <button
              key={item.name}
              onClick={() => handleNavigation(item.id, item.href)}
              className={`block w-full text-left px-5 py-3 rounded-xl text-base font-bold transition-all ${currentPage === item.id
                ? 'bg-primary-50 text-primary-600'
                : 'text-neutral-600 hover:bg-neutral-50'
                }`}
            >
              {item.name}
            </button>
          ))}

          <div className="pt-4 mt-4 border-t border-neutral-50 space-y-3">
            {!isAuthenticated ? (
              <>
                <button
                  onClick={() => { setMobileMenuOpen(false); onLogin(); }}
                  className="block w-full px-5 py-3 rounded-xl text-base font-bold text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={() => { setMobileMenuOpen(false); onGetStarted(); }}
                  className="block w-full px-5 py-4 bg-primary-600 text-neutral-0 rounded-xl text-base font-bold shadow-soft text-center transition-transform active:scale-95"
                >
                  Get Started
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center space-x-3 px-5 py-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-neutral-0 shadow-sm text-lg font-bold">
                    {user?.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-sm font-bold text-neutral-900 truncate">{user?.email}</p>
                    <p className="text-xs text-neutral-400 capitalize">{user?.subscription_plan || 'Free'}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleNavigation('app', '/app')}
                  className="block w-full text-left px-5 py-3 rounded-xl text-base font-bold text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  Dashboard
                </button>
                <button
                  onClick={logout}
                  className="block w-full text-left px-5 py-3 rounded-xl text-base font-bold text-error/80 hover:bg-error/5 transition-colors"
                >
                  Sign Out
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default MarketingHeader;
